import Accordion from "@/components/Accordion";
import { LanguageConfigModel, StyleConfigModel } from "@/pages/Messaging/types";
import { Toggle } from "@/components/Toggle";
import Slider from "@/components/Slider";
import React from "react";
import TextInput from "../../TextInput";
import LogoUpload from "../../LogoUpload";
import { ChatWindow } from "../../types";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "pt", label: "Portuguese" },
  { value: "it", label: "Italian" },
  { value: "nl", label: "Dutch" },
  { value: "pl", label: "Polish" },
  { value: "ru", label: "Russian" },
  { value: "ja", label: "Japanese" },
  { value: "zh", label: "Chinese (Simplified)" },
  { value: "ko", label: "Korean" },
  { value: "ar", label: "Arabic" },
];

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
  setStagedHeaderBgFile: (file: File | null) => void;
}

export function Header(props: Props) {
  const workingStyleConfig = props.workingChatWindow.styleConfig || {};
  const workingLanguageConfig = props.workingChatWindow.languageConfig || {};

  const updateStyleConfig = (updates: Partial<StyleConfigModel>) => {
    props.setWorkingChatWindow({
      ...props.workingChatWindow,
      styleConfig: {
        ...workingStyleConfig,
        ...updates
      }
    });
  };

  const updateLanguageConfig = (updates: Partial<LanguageConfigModel>) => {
    props.setWorkingChatWindow({
      ...props.workingChatWindow,
      languageConfig: {
        ...workingLanguageConfig,
        ...updates
      }
    });
  };

  return (
    <Accordion.Item eventKey="header" onExpanded={() => props.onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Header</Accordion.Title>
        <Accordion.SubTitle>
          Customize the header section, where the initial interaction begins.
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="mb-2 space-y-4">
          <div className="flex justify-between items-center">
            <div className="text-sm font-medium">Logo</div>
            <Toggle
              toggled={workingStyleConfig.showLogo}
              changeToggle={() =>
                updateStyleConfig({
                  showLogo: !workingStyleConfig.showLogo
                })
              }
            />
          </div>

          <div className="border-t border-slate-200" />

          <div>
            <div className="text-sm font-medium mb-2">Title</div>
            <TextInput
              value={workingLanguageConfig.title}
              setValue={(value) => updateLanguageConfig({ title: value })}
              showEmojiButton={true}
              maxWordCount={60}
            />
          </div>

          <div>
            <div className="text-sm font-medium mb-2">Sub Title</div>
            <TextInput
              value={workingLanguageConfig.subtitle}
              setValue={(value) => updateLanguageConfig({ subtitle: value })}
              showEmojiButton={false}
              maxWordCount={60}
            />
          </div>

          <div className="border-t border-slate-200" />

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-medium">Auto-detect Visitor Language</div>
                <div className="text-xs text-slate-600">Translate chat UI to match the visitor's browser language. Translations are cached for 30 days to avoid repeated API calls.</div>
              </div>
              <Toggle
                toggled={workingStyleConfig.autoDetectVisitorLanguage ?? false}
                changeToggle={() =>
                  updateStyleConfig({ autoDetectVisitorLanguage: !workingStyleConfig.autoDetectVisitorLanguage })
                }
              />
            </div>
            {!(workingStyleConfig.autoDetectVisitorLanguage) && (
              <div>
                <div className="text-sm font-medium mb-2">Language</div>
                <select
                  aria-label="Language"
                  className="border border-slate-300 rounded-md p-2 w-full text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
                  value={workingLanguageConfig.language ?? "en"}
                  onChange={(e) => updateLanguageConfig({ language: e.target.value })}
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>{lang.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200" />

          <LogoUpload
            setStageLogoFile={props.setStagedHeaderBgFile}
            update={updateStyleConfig}
            workingStyleConfig={workingStyleConfig}
            field="headerBackgroundImageUrl"
            label="Header Background Image"
            previewClassName="w-full h-full object-cover"
            sizeGuidance="PNG or SVG • Wide/banner image works best • Displayed behind your header title"
          />

          <div className="border-t border-slate-200" />

          <div>
            <div className="text-sm font-medium mb-2">Header Image</div>
            <select
              aria-label="Header Image"
              className="border border-slate-300 rounded-md p-2 w-full text-sm"
              value={workingStyleConfig.headerImageType ?? "none"}
              onChange={(e) =>
                updateStyleConfig({
                  headerImageType: e.target.value as "agentHeadshot" | "companyLogo" | "none"
                })
              }
            >
              <option value="none">None</option>
              <option value="agentHeadshot">Agent Headshot</option>
              <option value="companyLogo">Company Logo</option>
            </select>
            {(workingStyleConfig.headerImageType ?? "none") !== "none" && (
              <div className="mt-3">
                <div className="flex justify-between items-center mb-1">
                  <div className="text-sm font-normal">Image Scale</div>
                  <div className="text-sm text-slate-500">{workingStyleConfig.headerImageScale ?? 100}%</div>
                </div>
                <Slider
                  min={50}
                  max={150}
                  value={workingStyleConfig.headerImageScale ?? 100}
                  onChange={(value) => updateStyleConfig({ headerImageScale: value })}
                />
              </div>
            )}
          </div>

          <div>
            <div className="text-sm font-medium mb-2">Chat Line Image</div>
            <select
              aria-label="Chat Line Image"
              className="border border-slate-300 rounded-md p-2 w-full text-sm"
              value={workingStyleConfig.chatLineImageType ?? "agentHeadshot"}
              onChange={(e) =>
                updateStyleConfig({
                  chatLineImageType: e.target.value as "agentHeadshot" | "companyLogo"
                })
              }
            >
              <option value="agentHeadshot">Agent Headshot</option>
              <option value="companyLogo">Company Logo</option>
            </select>
          </div>

          <div>
            <div className="text-sm font-medium mb-2">Popout Window Title</div>
            <TextInput
              value={workingLanguageConfig.popoutWindowTitle ?? ""}
              setValue={(value) => updateLanguageConfig({ popoutWindowTitle: value })}
              showEmojiButton={false}
              maxWordCount={60}
              ariaLabel="Popout Window Title"
            />
          </div>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
